import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { confirmDialog } from "@/lib/confirm";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldStar, Plus, Trash, PencilSimple, Copy, Check, Crown } from "@phosphor-icons/react";

/* ============================================================================
   CoeRolesTab — Roles & Permissions editor.
   ----------------------------------------------------------------------------
   Left pane:  list of roles (built-in immutable + custom editable)
   Right pane: permission grid (resources × actions checkbox matrix)

   Built-in roles are read-only — admin can clone them into a new custom role.
   Custom roles are fully editable + deletable (if no users assigned).
   ========================================================================== */

export function CoeRolesTab() {
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [cloneSource, setCloneSource] = useState(null); // built-in role being cloned

    const load = () => {
        api.get("/admin/coe/roles").then((r) => setRoles(r.data || [])).catch(() => {});
    };
    useEffect(() => {
        load();
        api.get("/admin/coe/roles/catalog").then((r) => setCatalog(r.data));
    }, []);

    const selected = useMemo(
        () => roles.find((r) => r.id === selectedId) || roles[0],
        [roles, selectedId]
    );

    return (
        <div className="grid lg:grid-cols-[280px,1fr] gap-5" data-testid="coe-roles-tab">
            {/* Left pane: roles list */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Roles</div>
                    <Button size="sm" onClick={() => { setCloneSource(null); setCreateOpen(true); }} className="h-7 rounded-full" data-testid="coe-roles-create">
                        <Plus size={11} className="mr-1" /> New
                    </Button>
                </div>
                <div className="max-h-[600px] overflow-y-auto">
                    {roles.map((r) => {
                        const on = (selected && selected.id === r.id);
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => setSelectedId(r.id)}
                                className={`w-full text-left px-4 py-2.5 flex items-center gap-2 border-b border-border last:border-0 transition ${on ? "bg-primary/5" : "hover:bg-secondary/40"}`}
                                data-testid={`coe-role-item-${r.is_built_in ? r.key : r.id}`}
                            >
                                <ShieldStar size={14} weight={r.is_built_in ? "fill" : "duotone"} className={r.is_built_in ? "text-amber-600" : "text-primary"} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                                        {r.name}
                                        {r.key === "admin" && <Crown size={10} weight="fill" className="text-amber-600" />}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {r.is_built_in ? "Built-in" : "Custom"} · {r.usage_count} user{r.usage_count === 1 ? "" : "s"}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Right pane: permission grid for the selected role */}
            <div>
                {selected && catalog ? (
                    <RoleEditor
                        role={selected}
                        catalog={catalog}
                        roles={roles}
                        onChanged={load}
                        onClone={() => { setCloneSource(selected); setCreateOpen(true); }}
                    />
                ) : (
                    <div className="text-muted-foreground text-sm text-center py-10">Loading roles…</div>
                )}
            </div>

            <CreateRoleDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                catalog={catalog}
                cloneSource={cloneSource}
                onCreated={(r) => { load(); setSelectedId(r.id); setCreateOpen(false); }}
            />
        </div>
    );
}

/* ---------- Role editor (permission grid) ---------- */
function RoleEditor({ role, catalog, roles, onChanged, onClone }) {
    const isBuiltIn = role.is_built_in;
    const [perms, setPerms] = useState(role.permissions || {});
    const [name, setName] = useState(role.name);
    const [desc, setDesc] = useState(role.description || "");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setPerms(role.permissions || {});
        setName(role.name);
        setDesc(role.description || "");
        setDirty(false);
    }, [role.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const isSuperuser = perms["*"]?.includes("*");

    const toggle = (resource, action) => {
        if (isBuiltIn) return;
        const current = new Set(perms[resource] || []);
        if (current.has(action)) current.delete(action); else current.add(action);
        const next = { ...perms };
        if (current.size === 0) delete next[resource];
        else next[resource] = Array.from(current);
        setPerms(next);
        setDirty(true);
    };

    const toggleRow = (resource) => {
        if (isBuiltIn) return;
        const isFull = (perms[resource] || []).length === catalog.actions.length;
        const next = { ...perms };
        if (isFull) delete next[resource];
        else next[resource] = [...catalog.actions];
        setPerms(next);
        setDirty(true);
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            await api.put(`/admin/coe/roles/${role.id}`, {
                name: name.trim(),
                description: desc.trim(),
                permissions: perms,
            });
            toast.success("Role saved");
            onChanged();
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    const remove = async () => {
        if (!(await confirmDialog({ title: `Delete role "${role.name}"?`, message: "This cannot be undone.", confirmText: "Delete role", tone: "danger" }))) return;
        try {
            await api.delete(`/admin/coe/roles/${role.id}`);
            toast.success("Role deleted");
            onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="coe-role-editor">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <Input
                        value={name}
                        onChange={(e) => { setName(e.target.value); setDirty(true); }}
                        disabled={isBuiltIn}
                        className="text-xl font-display font-bold tracking-tight h-12 border-0 px-0 focus-visible:ring-0 disabled:opacity-100 disabled:cursor-default"
                        data-testid="coe-role-name"
                    />
                    <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mt-1">
                        {isBuiltIn ? "Built-in (read-only)" : "Custom role"} · {role.usage_count} user{role.usage_count === 1 ? "" : "s"}
                    </div>
                </div>
                <div className="flex gap-2">
                    {isBuiltIn ? (
                        <Button onClick={onClone} variant="outline" className="rounded-full" data-testid="coe-role-clone">
                            <Copy size={13} className="mr-1.5" /> Clone to edit
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={remove} className="rounded-full text-rose-600 border-rose-200" data-testid="coe-role-delete">
                                <Trash size={13} className="mr-1.5" /> Delete
                            </Button>
                            <Button onClick={save} disabled={saving || !dirty} className="rounded-full" data-testid="coe-role-save">
                                <Check size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <Textarea
                value={desc}
                onChange={(e) => { setDesc(e.target.value); setDirty(true); }}
                disabled={isBuiltIn}
                placeholder="Describe what this role can do…"
                rows={2}
                className="text-sm bg-secondary/30 disabled:opacity-100"
                data-testid="coe-role-description"
            />

            {isSuperuser ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-sm">
                    <Crown size={14} weight="fill" className="inline text-amber-600 mr-1.5" />
                    <span className="font-semibold text-amber-800">Superuser role.</span>
                    <span className="text-amber-700"> This role has unrestricted access to every resource. Cannot be modified.</span>
                </div>
            ) : (
                <PermissionGrid
                    catalog={catalog}
                    perms={perms}
                    readOnly={isBuiltIn}
                    onToggle={toggle}
                    onToggleRow={toggleRow}
                />
            )}
        </div>
    );
}

/* ---------- Permission grid ---------- */
function PermissionGrid({ catalog, perms, readOnly, onToggle, onToggleRow }) {
    return (
        <div className="rounded-xl border border-border overflow-x-auto" data-testid="coe-perm-grid">
            <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                    <tr>
                        <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">Resource</th>
                        {catalog.actions.map((a) => (
                            <th key={a} className="px-2 py-2 text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground text-center w-16">{a}</th>
                        ))}
                        <th className="px-2 py-2 text-center w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {catalog.resources.map((res) => {
                        const acts = new Set(perms[res.key] || []);
                        const allOn = acts.size === catalog.actions.length;
                        const someOn = acts.size > 0 && !allOn;
                        return (
                            <tr key={res.key} className="border-t border-border">
                                <td className="px-3 py-2 align-top">
                                    <div className="font-mono uppercase text-[11px] tracking-wide font-bold">{res.key}</div>
                                    <div className="text-[11px] text-muted-foreground">{res.label}</div>
                                </td>
                                {catalog.actions.map((a) => (
                                    <td key={a} className="text-center">
                                        <input
                                            type="checkbox"
                                            checked={acts.has(a)}
                                            onChange={() => onToggle(res.key, a)}
                                            disabled={readOnly}
                                            className="w-4 h-4 rounded border-border accent-primary cursor-pointer disabled:cursor-not-allowed"
                                            data-testid={`coe-perm-${res.key}-${a}`}
                                            aria-label={`${res.key} ${a}`}
                                        />
                                    </td>
                                ))}
                                <td className="text-center">
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => onToggleRow(res.key)}
                                            className="text-[10px] uppercase tracking-wider font-bold text-primary hover:text-primary/80"
                                            data-testid={`coe-perm-row-toggle-${res.key}`}
                                        >
                                            {allOn ? "Clear" : "All"}
                                        </button>
                                    )}
                                    {readOnly && someOn && (
                                        <span className="text-[9px] uppercase font-bold text-muted-foreground">partial</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ---------- Create role dialog ---------- */
function CreateRoleDialog({ open, onClose, catalog, cloneSource, onCreated }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [cloneOf, setCloneOf] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(cloneSource ? `${cloneSource.name} (Copy)` : "");
            setDesc(cloneSource ? cloneSource.description : "");
            setCloneOf(cloneSource?.key || "");
            setSaving(false);
        }
    }, [open, cloneSource]);

    const submit = async () => {
        if (!name.trim()) { return; }
        setSaving(true);
        try {
            const r = await api.post("/admin/coe/roles", {
                name: name.trim(), description: desc.trim(),
                permissions: {}, clone_of: cloneOf || undefined,
            });
            onCreated(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="coe-create-role-dialog">
                <DialogHeader>
                    <DialogTitle>New role</DialogTitle>
                    <DialogDescription>
                        Clone a built-in role for a head start, or build permissions from scratch on the next screen.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Name *</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="coe-create-role-name" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Description</Label>
                        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className="mt-1" data-testid="coe-create-role-desc" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Clone permissions from</Label>
                        <select value={cloneOf} onChange={(e) => setCloneOf(e.target.value)}
                            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                            data-testid="coe-create-role-clone-of">
                            <option value="">— Start blank —</option>
                            {(catalog?.built_in_keys || []).filter((k) => k !== "admin").map((k) => (
                                <option key={k} value={k}>{k}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={!name.trim() || saving} data-testid="coe-create-role-submit">
                        {saving ? "Creating…" : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
